import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { ConfirmAction } from './types';

const confirmCopy = (
  c: ConfirmAction | null,
  name: string,
): { title: string; description: string; destructive: boolean; reason?: boolean } => {
  switch (c?.kind) {
    case 'duplicate':
      return {
        title: `Duplicate “${name}”?`,
        description:
          'This creates a draft copy owned by the same seller, carrying the spec, photos and prices. It has no verification, promotion or reach of its own, and is not published.',
        destructive: false,
        // Acting on a seller's listing on their behalf is audited, and an audit
        // row that says only "an admin did this" answers nothing later.
        reason: true,
      };
    case 'transition':
      return {
        title: `${c.label[0].toUpperCase()}${c.label.slice(1)} “${name}”?`,
        description: `This moves the listing to ${c.status}. The owner is notified of moderation changes.`,
        destructive: c.status !== 'LIVE',
      };
    case 'delete':
      return {
        title: `Delete “${name}”?`,
        description: 'This soft-deletes the listing (status → DELETED). It disappears from public results immediately but can be restored.',
        destructive: true,
      };
    case 'undelete':
      return { title: `Restore “${name}”?`, description: 'This restores the soft-deleted listing so it can appear publicly again.', destructive: false };
    case 'boost':
      return { title: `Boost “${name}”?`, description: 'This creates an admin promotion for the listing (no payment required).', destructive: false };
    case 'flag':
      return {
        title: `${c.current ? 'Disable' : 'Enable'} ${c.label}?`,
        description: `This turns ${c.current ? 'off' : 'on'} the “${c.label}” flag for this listing.`,
        destructive: false,
      };
    case 'asset-delete':
      return { title: 'Archive this photo?', description: 'This hides the photo from the listing but keeps it — you can restore it here anytime. The photo is not permanently deleted.', destructive: false };
    case 'asset-restore':
      return { title: 'Restore this photo?', description: 'This restores the archived photo back onto the listing.', destructive: false };
    default:
      return { title: '', description: '', destructive: false };
  }
};

/** Unified confirm dialog — every mutating action routes through here. */
export const ConfirmDialog: React.FC<{
  confirm: ConfirmAction | null;
  name: string;
  busy: boolean;
  onCancel: () => void;
  /** Actions whose copy sets `reason` pass what the admin typed. */
  onConfirm: (reason?: string) => void;
}> = ({ confirm, name, busy, onCancel, onConfirm }) => {
  const copy = confirmCopy(confirm, name);
  const [reason, setReason] = React.useState('');

  // A stale reason must not ride along to the next action that asks for one.
  React.useEffect(() => setReason(''), [confirm]);

  return (
    <AlertDialog open={!!confirm} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy.title}</AlertDialogTitle>
          <AlertDialogDescription>{copy.description}</AlertDialogDescription>
        </AlertDialogHeader>
        {copy.reason && (
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">Reason</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Why you are doing this, for the audit trail"
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </label>
        )}
        <AlertDialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={copy.destructive ? 'destructive' : 'default'}
            disabled={busy || (copy.reason && !reason.trim())}
            onClick={() => onConfirm(reason.trim() || undefined)}
          >
            {busy ? 'Working…' : 'Confirm'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
